import { withStoreCampaign } from '@/lib/store-attribution';
export const siteUrl =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, '') || 'https://cleanor.app';

export const brandName = 'Cleanor Labs';
export const siteName = 'Cleanor Research Labs';
export const companyName = 'Cleanor Research Labs L.L.C-FZ';
export const contactEmail = 'support@cleanor.app';
export const siteLanguage = 'en';
export const openGraphLocale = 'en_US';
export const googleAnalyticsId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim();
// Meta (Facebook) Pixel id. Public by design (it ships in client HTML), so a
// literal fallback keeps the pixel live while still allowing per-env overrides.
export const googleSiteVerification =
  process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION?.trim() ||
  'wuQvDyKKVXdBrnhj-EUPL7QiOkuxacxYUShFFT3hkDo';
export const bingSiteVerification = process.env.NEXT_PUBLIC_BING_SITE_VERIFICATION?.trim();
export const facebookAppId = '1292751589425867';
export const facebookPageUrl = 'https://www.facebook.com/profile.php?id=61582022649132';
export const linkedInCompanyUrl = 'https://www.linkedin.com/company/cleanor-labs/';
export const xHandle = 'cleanorlabs';
export const xProfileUrl = `https://x.com/${xHandle}`;
export const instagramProfileUrl = 'https://www.instagram.com/cleanorlabs/';
export const youtubeChannelUrl = 'https://www.youtube.com/@cleanorlabs';
export const githubOrgHandle = 'cleanor-app';
export const githubOrgUrl = `https://github.com/${githubOrgHandle}`;

export type OpenSourceRepo = {
  name: string;
  href: string;
  language: string;
  tagline: string;
  description: string;
  /** Optional live surface this repo powers, shown as a secondary link. */
  liveLabel?: string;
  liveHref?: string;
  /** Published npm package, if any. */
  npmPackage?: string;
  npmHref?: string;
  /** Highlighted on the /github page and used to order the list. */
  featured?: boolean;
};

// Curated metadata for the public repositories under github.com/cleanor-app.
// Kept static (not fetched at runtime) so the pages stay fast + crawlable and
// never depend on the GitHub API. Update by hand when a repo is added.
export const openSourceRepos: OpenSourceRepo[] = [
  {
    name: 'browser-image-tools',
    href: `${githubOrgUrl}/browser-image-tools`,
    language: 'TypeScript',
    tagline: 'Client-side image processing library',
    description:
      'Privacy-first image processing in the browser: convert and compress PNG, JPEG, WebP, AVIF, GIF, and ICO fully client-side. Files never leave the device.',
    liveLabel: 'Powers the Cleanor tools',
    liveHref: '/tools',
    npmPackage: '@cleanor/browser-image-tools',
    npmHref: 'https://www.npmjs.com/package/@cleanor/browser-image-tools',
    featured: true,
  },
  {
    name: 'cleanor-storage-lab',
    href: `${githubOrgUrl}/cleanor-storage-lab`,
    language: 'JavaScript',
    tagline: 'Open datasets & reproducible benchmarks',
    description:
      'Reproducible benchmarks and open datasets: image compression (JPEG/WebP/AVIF/JPEG XL), the HEIC-to-JPG size tax, cloud storage cost per GB, and phone storage capacity.',
    liveLabel: 'See the studies',
    liveHref: '/research',
    featured: true,
  },
  {
    name: 'cleanor-mcp',
    href: `${githubOrgUrl}/cleanor-mcp`,
    language: 'TypeScript',
    tagline: 'Zero-auth MCP server for AI builders',
    description:
      'The source behind mcp.cleanor.app: a zero-auth hosted MCP server that lets AI builders optimize and convert images, pull cited storage and image-format data, and generate QR codes.',
    liveLabel: 'MCP server page',
    liveHref: '/mcp',
    npmPackage: '@cleanor/mcp',
    npmHref: 'https://www.npmjs.com/package/@cleanor/mcp',
    featured: true,
  },
  {
    name: 'cleanor-tools',
    href: `${githubOrgUrl}/wordpress-image-optimizer`,
    language: 'PHP',
    tagline: 'WordPress image optimizer plugin',
    description:
      'A WordPress image optimizer that auto-converts Media Library images to WebP and AVIF for faster pages, better Core Web Vitals, and less storage. No account or API key.',
    liveLabel: 'WordPress plugin page',
    liveHref: '/wordpress',
  },
  {
    name: 'awesome-mcp-servers',
    href: `${githubOrgUrl}/awesome-mcp-servers`,
    language: 'Markdown',
    tagline: 'Curated list of MCP servers',
    description:
      'A curated collection of Model Context Protocol servers for AI builders exploring the wider MCP ecosystem.',
  },
];
export const currentPathHeader = 'x-current-pathname';
export const clenoirAppId = '6744980716';
export const clenoirAppStoreUrl = `https://apps.apple.com/us/app/cleanor-smart-phone-cleaner/id${clenoirAppId}`;
export const clenoirTeamIds = {
  current: 'B9MA6633AK',
  legacy: '4C548YJN9X',
} as const;
export const clenoirBundleId = 'com.le.clear.noir.LeCleanoir';
export const clenoirAppClipBundleId = `${clenoirBundleId}.Clip`;
export const clenoirCurrentAssociatedAppId = `${clenoirTeamIds.current}.${clenoirBundleId}`;
export const clenoirLegacyAssociatedAppId = `${clenoirTeamIds.legacy}.${clenoirBundleId}`;
export const clenoirAppClipAssociatedAppId = `${clenoirTeamIds.current}.${clenoirAppClipBundleId}`;
export const clenoirAllAssociatedAppIds = [
  clenoirCurrentAssociatedAppId,
  clenoirLegacyAssociatedAppId,
];
export const clenoirProjectPath = '/cleanor-ios';
export const cleanorIosAppClipMetaContent = `app-id=${clenoirAppId}, app-clip-bundle-id=${clenoirAppClipBundleId}, app-clip-display=card`;
export const cleanorIosSmartBannerArgument = `${siteUrl}${clenoirProjectPath}`;
export const defaultOgImage = `${siteUrl}/og/default.jpg`;
export const defaultOgImageWidth = 1200;
export const defaultOgImageHeight = 630;
export const defaultOgImageAlt = 'Cleanor: free, private file and image tools';

// Per-page branded OG card (1200×630), generated into public/og/ by
// scripts/generate-og.mjs. `key` is the file stem (e.g. 'about', 'tools-pdf-tools',
// 'blog-<slug>'). Falls back to the default card for keys without a generated file.
export const ogImageUrl = (key: string) => `${siteUrl}/og/${key}.jpg`;
export const rssFeedUrl = `${siteUrl}/feed.xml`;
export const atomFeedUrl = `${siteUrl}/atom.xml`;
export const llmsUrl = `${siteUrl}/llms.txt`;
export const llmsFullUrl = `${siteUrl}/llms-full.txt`;
export const indexNowKey = 'f426ca5152cb4e2c91bb12ae809c3a4b';
export const indexNowKeyFilePath = `/${indexNowKey}.txt`;
export const indexNowKeyFileUrl = `${siteUrl}${indexNowKeyFilePath}`;
export const indexNowEndpoint = 'https://api.indexnow.org/indexnow';
// Bump this whenever a batch of pages ships. It stamps the <lastmod> on the
// sitemap index and the core/tools/reference/discovery child sitemaps, so a
// stale value makes crawlers think nothing changed and de-prioritizes re-crawl
// of newly added tool/reference URLs.
export const siteUpdatedAt = '2026-08-01T00:00:00.000Z';
export const privacyPolicyUpdatedAt = '2026-07-07T00:00:00.000Z';
export const termsUpdatedAt = '2026-07-07T00:00:00.000Z';
export const refundPolicyUpdatedAt = '2026-07-05T00:00:00.000Z';
export const accessibilityUpdatedAt = '2026-04-21T00:00:00.000Z';

// Cleanor for Android, live on Google Play as app.cleanor.phonecleaner. The
// earlier com.cleanorlabs.cleanor id was never published and is gone from the
// asset-links file; keeping it there pointed Digital Asset Links at a 404.
export const androidPackageName = 'app.cleanor.phonecleaner';
// Play App Signing certificate (Play Console > App integrity). This is the one
// Google checks for App Links on installs that came from Play.
export const androidReleaseSha256 =
  '73:24:0F:46:94:EE:3D:7A:49:AF:59:4C:6E:F9:E3:B6:BC:D9:01:42:0E:5A:B0:0E:08:7C:84:87:11:35:0B:48';
// Upload key certificate, so builds installed straight from a signed bundle
// (internal testing, sideloaded QA) verify the same links.
export const androidUploadSha256 =
  '4D:72:34:3C:B8:41:A1:25:52:E8:02:63:5D:72:88:23:B8:C2:A2:26:5D:2A:BB:90:F1:00:C5:D2:50:08:AA:21';
export const cleanorAndroidPlayUrl = `https://play.google.com/store/apps/details?id=${androidPackageName}`;
// Declared this early (rather than with the other entity profiles below) because
// cleanorIosLanding references it — a later `const` would be a TDZ error.
export const productHuntUrl = 'https://www.producthunt.com/products/cleanor-smart-phone-cleaner';
export const photoCompressorAndroidPackageName = 'app.cleanor.photocompressor';
export const photoCompressorAndroidReleaseSha256 =
  '05:11:1E:D6:26:62:40:98:BB:60:EC:C7:85:2C:6A:A9:5D:0B:67:43:43:38:05:2A:29:EA:CA:4D:99:2C:2C:E8';
export const photoCompressorProjectPath = '/photo-compressor-resizer';
export const videoCompressorAndroidPackageName = 'com.cleanorlabs.videocompressor';
export const videoCompressorAndroidReleaseSha256 =
  '67:08:62:DB:92:EA:2E:35:79:F6:81:98:C2:14:61:CB:68:7D:F9:72:63:F3:5D:53:15:C9:F1:83:BD:0A:B0:88';
export const videoCompressorProjectPath = '/video-compressor-resizer';
export const mindAlarmAndroidPackageName = 'com.cleanorlabs.mindalarm';

export type ProjectSlug =
  | 'clenoir'
  | 'cleanor'
  | 'mindalarm'
  | 'image-to-pdf-converter'
  | 'pdf-scanner-editor'
  | 'photo-compressor-resizer'
  | 'video-compressor-resizer'
  | 'another-you';
export type ProjectRouteSlug =
  | 'cleanor-ios'
  | 'cleanor'
  | 'alarm-clock-focus-calm-sleep'
  | 'image-to-pdf-converter'
  | 'pdf-scanner-editor'
  | 'photo-compressor-resizer'
  | 'video-compressor-resizer'
  | 'another-you';

export type Project = {
  slug: ProjectSlug;
  routeSlug: ProjectRouteSlug;
  legacyRouteSlugs?: string[];
  name: string;
  stage: 'Live' | 'In review' | 'Coming soon';
  platform: string;
  category: string;
  operatingSystem: string;
  headline: string;
  summary: string;
  description: string[];
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  highlights: string[];
  href?: string;
  androidApplicationId?: string;
  ctaLabel: string;
  /**
   * Real, externally verifiable store rating. Only set this from genuine
   * store data (e.g. the App Store rating); it drives review rich results,
   * so fabricated numbers would violate Google's structured-data policy.
   */
  rating?: ProjectRating;
  landing?: ProjectLandingContent;
};

export type ProjectRating = {
  /** Average rating on a 1–5 scale. */
  value: number;
  /** Number of ratings the average is based on. */
  count: number;
};

/**
 * Builds a schema.org AggregateRating node from a project's store rating, or
 * returns undefined when there is no genuine rating to show. Shared by the home
 * page and the app project pages so the markup and the visible badge stay in
 * sync.
 */
export function buildAppAggregateRatingSchema(rating?: ProjectRating) {
  if (!rating || rating.count <= 0) {
    return undefined;
  }
  return {
    '@type': 'AggregateRating',
    ratingValue: rating.value,
    ratingCount: rating.count,
    bestRating: 5,
    worstRating: 1,
  };
}

export type ProjectLandingFeature = {
  title: string;
  description: string;
  bullets: string[];
};

export type ProjectLandingScreenshot = {
  src: string;
  alt: string;
  caption: string;
};

export type ProjectLandingQuickAction = {
  slug: string;
  title: string;
  description: string;
};

export type ProjectLandingStep = {
  title: string;
  description: string;
};

export type ProjectLandingFaq = {
  question: string;
  answer: string;
};

export type ProjectLandingContent = {
  storeLabel: string;
  storeAvailability: string;
  developerName: string;
  iconSrc: string;
  iconAlt: string;
  heroSummary: string;
  intro: string[];
  highlights: string[];
  features: ProjectLandingFeature[];
  screenshots: ProjectLandingScreenshot[];
  quickActions: ProjectLandingQuickAction[];
  trustChips?: string[];
  facts?: { label: string; value: string }[];
  steps?: ProjectLandingStep[];
  faqs?: ProjectLandingFaq[];
  googlePlayHref?: string;
  googlePlayLabel?: string;
  productHuntHref?: string;
};

const photoCompressorLanding: ProjectLandingContent = {
  storeLabel: 'Google Play',
  storeAvailability: 'Free on Google Play',
  developerName: 'Cleanor Research Labs',
  iconSrc:
    'https://play-lh.googleusercontent.com/QUO3OwMVq6HoMkTZ1l-qzKYA6PWGkRj29-SDTvVJjn3zUWPVrZV67e8Rv7fwkIijW_mNmKq82DVKaGhCVsZR2m8=w240-h480',
  iconAlt: 'Photo Compressor & Resizer app icon',
  heroSummary:
    'Reduce photo size, resize images, and export lighter files for sharing, posting, storage cleanup, and faster uploads.',
  intro: [
    'Photo Compressor & Resizer is built for the everyday image-prep jobs that usually should not require a desktop workflow. Open a photo, choose the output you need, and get to a smaller, cleaner file in a few taps.',
    'The app focuses on speed and clarity: compress heavy images, resize for platform requirements, crop before export, convert formats, and clean metadata when you want a leaner file for sharing.',
  ],
  highlights: [
    'Compress photos without turning the flow into a complicated editor',
    'Resize with custom dimensions or quick presets for common platforms',
    'Batch-process multiple images when one-by-one cleanup is too slow',
    'Crop, convert, adjust, and remove metadata before export',
  ],
  features: [
    {
      title: 'Compress photos fast',
      description:
        'Shrink image size for messaging, forms, uploads, and lighter storage use without dragging the job through unnecessary steps.',
      bullets: [
        'Choose a smaller output target quickly',
        'Keep the visual result usable for day-to-day sharing',
        'Run bulk compression when you have more than one image',
      ],
    },
    {
      title: 'Resize for the destination',
      description:
        'Set the width and height you need when a website, marketplace, form, or social surface expects a specific image size.',
      bullets: [
        'Enter custom dimensions',
        'Use ready-made size presets',
        'Keep aspect ratio aligned while resizing',
      ],
    },
    {
      title: 'Do more than one job in one flow',
      description:
        'The app goes beyond simple file shrinking by giving you the smaller cleanup steps people often need before an image is ready to send or upload.',
      bullets: [
        'Crop before export',
        'Convert between JPG, PNG, and WEBP',
        'Adjust brightness and contrast when needed',
      ],
    },
    {
      title: 'Prepare safer exports',
      description:
        'When the visible image is fine but the hidden file baggage is not, you can export a leaner result with less metadata attached.',
      bullets: [
        'Strip location and camera data',
        'Save back to device or app storage',
        'Share the result immediately after processing',
      ],
    },
  ],
  screenshots: [
    {
      src: 'https://play-lh.googleusercontent.com/yf7vsdbAnaRWVn6vU5bboQIxYAegadWMWWiIVMjdFIc4p62PUNGwTAtqD48ejcUFzvNg3wd293Q2doec4vZB',
      alt: 'Photo Compressor & Resizer screenshot showing image tools home screen',
      caption: 'Start from the core image tools in one compact Android workflow.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/KNExqoEmdn7FxW9Lssv_2d4Arjeb_M6a9yt0ZMSduTTBw1D0fP0ygSqD_iUBWKWawfdLODeGkNG52ggwLp_yYl4',
      alt: 'Photo Compressor & Resizer screenshot showing batch compress queue',
      caption:
        'Queue multiple photos and run batch compression when one-by-one export is too slow.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/OGpDVVtYcvE8nB3droD6hY3lxQIq5K9Y99JWFuqV9rk4R20wtEtlDiXQfTiAJZgpmjrvufpU21OYtOfqicy5eg',
      alt: 'Photo Compressor & Resizer screenshot showing compression size setup',
      caption: 'Set the output size and see the estimated result before saving.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/hXfaGgsmR442X9Gc76HHpWqUywU4zUobi3GJTSLQXCZ8s7QS2Qa94pEFl3ve3maIfInEvkGw2s4W-S4rAQ5s3Q',
      alt: 'Photo Compressor & Resizer screenshot showing crop tool',
      caption: 'Crop the frame before export when the image needs a cleaner composition.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/YCEbvOJZ3VaTt3E4E-5WcM6bwFVHcVs2YW-L5DTA00A-Sx7zKMCHsIplkqG_j7tqsYYb44aLAL_YFgb3v6zUNg',
      alt: 'Photo Compressor & Resizer screenshot showing JPG PNG WEBP conversion',
      caption: 'Switch between JPG, PNG, and WEBP depending on where the file is going next.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/S3wULKpqJ7dBuycGnJrXZ-xPoeFv2EfOLCJqk-Yn7xBUuwV9SYp-Au-uW8ZSlGDYMoWrqLv003hT-Rtj6YuF',
      alt: 'Photo Compressor & Resizer screenshot showing metadata removal',
      caption: 'Remove metadata before sharing when privacy matters more than file history.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/VKa4E3-Oi7XA5KtdOYC659-ZtLQFJ1-b-K_FNTFtRr5-50_Ms0s7ATv8Cl_9vO29dQSz2R6HsCV5kY-Q0o87IzY',
      alt: 'Photo Compressor & Resizer screenshot showing quick photo adjustments',
      caption: 'Make quick brightness and contrast changes before you export the final image.',
    },
  ],
  quickActions: [
    {
      slug: 'compress',
      title: 'Compress',
      description: 'Open the app straight into file-size reduction for one photo or a batch.',
    },
    {
      slug: 'resize',
      title: 'Resize',
      description: 'Jump into width and height controls for platform-specific image sizing.',
    },
    {
      slug: 'crop',
      title: 'Crop',
      description:
        'Start from the crop tool when the frame needs a faster cleanup than full editing.',
    },
    {
      slug: 'convert',
      title: 'Convert',
      description: 'Open the format-conversion flow for JPG, PNG, and WEBP exports.',
    },
  ],
};

const videoCompressorLanding: ProjectLandingContent = {
  storeLabel: 'Google Play',
  storeAvailability: 'Free on Google Play',
  developerName: 'Cleanor Research Labs',
  iconSrc:
    'https://play-lh.googleusercontent.com/cmwBnB8WP3e4M4yFb-dfafpof-buFcdafNqCyGUFWGoGxpNZQB1meQi_EZMHqgdFUtVUXbsh4ij6FtxfvpuoNQ=w240-h480',
  iconAlt: 'Video Compressor & Resizer app icon',
  heroSummary:
    'Compress videos fast, resize clips, convert formats, and make heavy files easier to share without dragging quality through a bloated editor.',
  intro: [
    'Video Compressor & Resizer is built for the common phone problem of clips that are too large to send, upload, or keep around comfortably. The app keeps the flow focused: pick a video, shrink the file, convert the format if needed, and get back to sharing.',
    'The Google Play listing also leans into practical media prep beyond simple compression. You can batch-process multiple files, trim before export, convert video to MP4, pull audio out as MP3, and prep clips for WhatsApp, Instagram, TikTok, email, or lighter device storage.',
  ],
  highlights: [
    'Shrink large videos without making the result feel broken',
    'Resize and convert clips for social, messaging, and storage limits',
    'Trim, extract audio, and batch-process from one Android workflow',
    'Built for on-device work with no internet dependency',
  ],
  features: [
    {
      title: 'Compress heavy clips quickly',
      description:
        'Reduce video size when the original file is too large for upload, messaging, or practical storage on the phone.',
      bullets: [
        'Cut file weight for faster sharing',
        'Keep visible quality strong after export',
        'Handle more than one file when needed',
      ],
    },
    {
      title: 'Convert into the format you need',
      description:
        'Switch between common video formats and move toward MP4 when you need broader compatibility across apps and devices.',
      bullets: [
        'Convert from MOV, AVI, MKV, WMV, and more',
        'Create MP4 output for easier playback',
        'Prepare files for email, web upload, and social posting',
      ],
    },
    {
      title: 'Trim and extract before export',
      description:
        'Clean up the clip before you save it by cutting unnecessary sections or turning the video into an MP3 when only the audio matters.',
      bullets: [
        'Trim away dead space before compression',
        'Extract MP3 from video clips',
        'Create lightweight outputs for reuse and sharing',
      ],
    },
    {
      title: 'Built for mobile-first sharing',
      description:
        'The app is optimized around the places where oversized video files usually become a problem first: chat, social posting, and limited device storage.',
      bullets: [
        'Prep videos for WhatsApp, Instagram, and TikTok',
        'Run processing offline on device',
        'Tune resolution and bitrate when needed',
      ],
    },
  ],
  screenshots: [
    {
      src: 'https://play-lh.googleusercontent.com/u-5ejlaO0QpcwhSRlCFjKg0dy1DSUjTHI-1isQheRZIWD0YRCGwamN9WM_2rzFS7gN5uilVAp6PevQcDsRY2Pg',
      alt: 'Video Compressor & Resizer screenshot showing batch compression queue',
      caption: 'Queue multiple videos and compress them in one pass.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/3Ie4vIssAc51RyN7-2nGt05fRdBdLpsuPkBLsnFxQUCfxutJovEZtDYKUCQWZb9Htn0QGEiqLK6j8DC0tst8',
      alt: 'Video Compressor & Resizer screenshot showing output size setup',
      caption: 'Set the target size before export and keep the result predictable.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/QIGK1qLj53i5blU9T1au9p8d2EAk4IXYpoJX_LlE8z1naMnLsmKiHT8oW3D02ArnfY7-vyMDjc9k2kh0_Aezdx4',
      alt: 'Video Compressor & Resizer screenshot showing trim video tool',
      caption: 'Trim the clip before compression when only the useful moment should stay.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/JSYQsWT5EPsfX-7CE_g_pXrnPn6L7SnFo5Tl4V8sgm--pULAFi-uxvfW48783US5PJyDYTzUvwbVruspVqStVw',
      alt: 'Video Compressor & Resizer screenshot showing video format conversion options',
      caption: 'Convert video output into the format that fits the next app or platform.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/UJpESVK9amGitJpOQN3KPrGfsKsBVlXNO9RL9GrqMFOBjaGbqwEThqNY1y1LGGQ0lElAEmnNV3LEEtE_41lJ',
      alt: 'Video Compressor & Resizer screenshot showing audio extraction flow',
      caption: 'Pull MP3 audio out of a video when the sound is what matters.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/k4XpXRxeL7h-FOWAGYXE18tl3Miz0HL-ZG0p7qKnyrJZzPUOyqmUxe-kA_O79yp9GqFth3mfQPMotYfL_0PD0w',
      alt: 'Video Compressor & Resizer screenshot showing GIF maker tool',
      caption: 'Turn short segments into GIFs for quick social sharing.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/QJ3Pobi1yuyuQRs4QSYkZ5sn4GaQcnSDgy7kOzo5xFGDDjKdYXaqfCpHJv6q4BHK9xz4EnRPX5Vkm1HDYPzoyg',
      alt: 'Video Compressor & Resizer screenshot showing custom quality controls',
      caption: 'Adjust resolution and bitrate when you need a more exact balance.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/8h3PIREKlFlMx9pih4IMQI1cGeTs-NuDze4Fq4RHTEcRopZGq5BrVs6mD9oa7sNPjWk-1eGPJ4JxFYdtuWCTTQ',
      alt: 'Video Compressor & Resizer screenshot showing saved output options',
      caption: 'Save lighter exports back to the phone and share them immediately.',
    },
  ],
  quickActions: [],
};

const mindAlarmLanding: ProjectLandingContent = {
  storeLabel: 'Google Play',
  storeAvailability: 'Free on Google Play',
  developerName: 'Cleanor Research Labs',
  iconSrc:
    'https://play-lh.googleusercontent.com/o9IUb3nClMbzyOzBz1afN-vork7nn1SwK8i4hGJC8s_zrnSCY-Ims4zq2RzLo9C17drq8ZBeNEzDtXZt5NC4GLc=w240-h480',
  iconAlt: 'MindAlarm app icon',
  heroSummary:
    'A calmer Android alarm clock with wake-up challenges, focus sessions, breathing tools, and sleep soundscapes in one cleaner routine app.',
  intro: [
    'MindAlarm is positioned as more than a basic alarm clock. The Google Play listing frames it as a smart wake-up app for heavy sleepers, students, and professionals who want a gentler start to the day without losing reliability.',
    'The product also stretches beyond alarms into daily rhythm tools: mission-based dismiss actions, focus timer sessions, breathing exercises, ambient soundscapes, and sleep-oriented audio. The result is a single Android app for waking up, locking in, and winding down.',
  ],
  highlights: [
    'Mission alarms for people who usually snooze through standard wake-ups',
    'Focus timer sessions for work, study, and distraction control',
    'Breathing, ambient sound, and sleep support in the same app',
    'Offline-friendly routines with strong personalization',
  ],
  features: [
    {
      title: 'Wake up with intent',
      description:
        'The alarm flow is built to do more than make noise. It pushes you to engage with the wake-up so getting out of bed becomes harder to ignore.',
      bullets: [
        'Use math and memory challenges to dismiss alarms',
        'Control snooze behavior more aggressively',
        'Wake up to custom sounds, music, or calmer audio',
      ],
    },
    {
      title: 'Stay locked in during the day',
      description:
        'MindAlarm also works as a focused work and study timer, so the app stays useful after the morning alarm is done.',
      bullets: [
        'Run distraction-light focus sessions',
        'Use soundscapes and noise layers while working',
        'Support routines for study, ADHD, and deep concentration',
      ],
    },
    {
      title: 'Wind down more cleanly',
      description:
        'The app includes breathing and audio tools that make it useful at night too, not only when it is time to wake up again.',
      bullets: [
        'Follow guided breathing modes',
        'Use white noise and ambient sleep sounds',
        'Play AI music for relaxation and sleep',
      ],
    },
    {
      title: 'Personalize the routine',
      description:
        'From wallpapers to ringtone choices and challenge difficulty, the app leans into customization so the routine feels yours instead of generic.',
      bullets: [
        'Choose from themed wallpapers and backgrounds',
        'Tune challenge difficulty and snooze behavior',
        'Keep alarms and focus sessions stored locally offline',
      ],
    },
  ],
  screenshots: [
    {
      src: 'https://play-lh.googleusercontent.com/Cf48vb3EtViqURQgp8pbDvhXHCqBpci0QN9wCp9IDGhmymwlwHT3Zgu1FeWoh9EB-lS-dUXIi9MWyrSm4y_c_7g',
      alt: 'MindAlarm screenshot showing alarm dashboard',
      caption: 'Start from a wake-up dashboard built around cleaner morning control.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/8plyJ0pJKyXM28_0zrfu9ufVqMVESWOMFI0rwEYKdeQ_lzqLZZFs5_5L9HDESaFKIBPDo2mjxBrF2ex_0IAnuw',
      alt: 'MindAlarm screenshot showing mission alarm settings',
      caption: 'Set alarm missions that make it harder to sleep through the wake-up.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/pxarajScgxHO7yk5rNWzwvHwnKaJE-_5BDBe-E9GGjus6oKkoPMxkd9c-9FVmF2jjwTqD_TTSj37NuIs0Tmhzw',
      alt: 'MindAlarm screenshot showing focus timer session',
      caption: 'Move from alarms into focused work and study sessions inside the same app.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/GCApOrRV__nBZanNdRaj0KU-iuaRYXfF5QQRwlZf-babY6lr4XNYe9smewb4lmLQb_5GybQjkTEvIjN-MN_WpQ',
      alt: 'MindAlarm screenshot showing breathing or sleep mode',
      caption: 'Use breathing and sound modes when the goal is to calm down, not speed up.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/O83BDP96ZClCNbn-2bewpzM12jiKTFeghrAC-SqUyzt9SSZbL-XExF66oTxfY2bab2NQ2JkLJVGqowYBp4s1cg',
      alt: 'MindAlarm screenshot showing soundscape selection',
      caption: 'Choose the ambient background that fits sleep, focus, or wake-up time.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/3kcS98QSDjrBz_FZaEXbZvQLO3c0ifuX9NrxtZg-voOGu5oIiLQlrgsrP4rn0JhhjTqe-jGxQXDWf0ISuBrV9g',
      alt: 'MindAlarm screenshot showing customization options',
      caption: 'Tune the alarm experience around wallpapers, sounds, and routine settings.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/XeyffNmZITrwbq0l8TsnXjC95TMbb8v0WZ5Sxzi8ZKRBebPbthuOr14vOybI_8lqPkusMQPV_sVVCZdQN_Nf',
      alt: 'MindAlarm screenshot showing challenge difficulty options',
      caption: 'Adjust challenge intensity to match how hard it is to wake up.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/W7naV3yrdCqcl5sEBZb1KdKK8qkfxpU0mMjV9XXmX-GNLGwzk0s8GSWFOsu4C7grw1Xi7RRJGU9p7LoyFfFq',
      alt: 'MindAlarm screenshot showing relaxation tools',
      caption: 'Keep the same app for focus, relaxation, and better sleep hygiene.',
    },
  ],
  quickActions: [],
};

const pdfScannerLanding: ProjectLandingContent = {
  storeLabel: 'Google Play',
  storeAvailability: 'Free on Google Play',
  developerName: 'Cleanor Research Labs',
  iconSrc:
    'https://play-lh.googleusercontent.com/-CoCWJYWBmbKcYSFeePA1_KywHnWvOiFHQ8bDJ_8SviCkAkVqfZWJAXfo1lHnl3P-AfRQKsu8ZPOJlJHtxDZsQ=w240-h480',
  iconAlt: 'PDF Scanner & Editor app icon',
  heroSummary:
    'Scan documents, convert images to PDF, extract text with OCR, and keep paperwork organized from one cleaner Android workflow.',
  intro: [
    'PDF Scanner & Editor is positioned as a pocket document scanner and PDF utility for students, professionals, and everyday admin work. The Google Play listing focuses on turning the phone into a practical capture-and-export tool without the usual office-suite heaviness.',
    'The app combines high-quality document scanning, image-to-PDF conversion, OCR text extraction, local-first file handling, and straightforward sharing. That makes it useful for receipts, notes, contracts, assignments, IDs, and any paper that should become a cleaner digital file.',
  ],
  highlights: [
    'Capture paper documents into cleaner PDF files on the phone',
    'Convert existing images into one combined PDF in seconds',
    'Extract searchable text with OCR from scans and photos',
    'Manage, rename, store, and share files without leaving the app',
  ],
  features: [
    {
      title: 'Scan pages with cleaner output',
      description:
        'The document capture flow focuses on readable, aligned scans so the phone result feels closer to a real scanner than a quick camera photo.',
      bullets: [
        'Detect document edges automatically',
        'Correct perspective for straighter pages',
        'Enhance text contrast for clearer reading',
      ],
    },
    {
      title: 'Turn images into PDF fast',
      description:
        'Use photos already on the device and combine them into one shareable PDF when scanning is not the starting point.',
      bullets: [
        'Merge multiple images into one file',
        'Build PDFs from receipts, photos, or screenshots',
        'Export a clean standardized document quickly',
      ],
    },
    {
      title: 'Pull text out with OCR',
      description:
        'The app also handles text extraction when you need the scan to become editable, searchable, or easier to reuse elsewhere.',
      bullets: [
        'Extract text from scanned pages',
        'Copy and share OCR output quickly',
        'Reduce manual typing for document data entry',
      ],
    },
    {
      title: 'Keep documents organized and private',
      description:
        'The listing leans on local processing and simple file management so sensitive documents stay easier to control.',
      bullets: [
        'Rename and categorize scanned files',
        'Store documents locally on device',
        'Share to email, messaging, or cloud storage when ready',
      ],
    },
  ],
  screenshots: [
    {
      src: 'https://play-lh.googleusercontent.com/HzWFToJ0ZxO95QAOEwlWKjspxcCnvQNmNP19En2jCcW7t6pNn3tF2j5se3SvUkuu7IKe8O9TmG1GTw_oVrozMhc',
      alt: 'PDF Scanner & Editor screenshot showing document scanner home screen',
      caption: 'Start from a mobile-first document scanner instead of a bloated office workflow.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/3NPjnjK-oz1foHQCzeuX-SxXsfCtv4a5xc692uPMl0DhNNfQNb4shrHhLMM3JClSEQIvXYnYFZUvwczQH4zvVVI',
      alt: 'PDF Scanner & Editor screenshot showing scan edge detection',
      caption: 'Capture pages with edge detection and perspective cleanup built in.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/OPF0-4FbTHeWzJ8zevrn7yEyT0024r1dg-jHJ5G9SJY0n57CGngra1_QkBwSiEabWErJnHr2ztITpJuut5zt',
      alt: 'PDF Scanner & Editor screenshot showing image to PDF conversion',
      caption:
        'Turn existing photos into a single PDF when the document is already in the gallery.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/B4S4sgu2zxX52IcP7g8QjVlRxt43XrE_wJJVqvb6Ug8xQEzfVX03LxvIYYeklEp036U8RorbOnV6LfVSDiOVDQ',
      alt: 'PDF Scanner & Editor screenshot showing OCR text extraction',
      caption: 'Extract text from scans so the document becomes easier to search and reuse.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/nopzerg7Od-5hmblshg49V9uzKqt4ZPV77LBPUn_Dgxy2JdIHsmXUZqzSPaedpVnormZB-l1xysiCknIraqtnx0',
      alt: 'PDF Scanner & Editor screenshot showing document filters',
      caption: 'Adjust scan filters for black and white, grayscale, or color output.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/hr0_YncHK_8V5swqHTbwDoqt0hfTF85vYcsOz6FmvuJsiu3JGVE5PXswd0S_I2ZUPKdsmjEXGGU0PbGOu1Xa',
      alt: 'PDF Scanner & Editor screenshot showing file manager',
      caption: 'Keep scanned documents organized from an in-app file manager.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/QgH2RKNsRDe4SI4Meld7mQnLRDr2nS6rOcMAahDMxYWGxO58ybnMIxuZs-S8-UsGXFf0-6wa5nEZS6x-McWFVw',
      alt: 'PDF Scanner & Editor screenshot showing export options',
      caption: 'Export as PDF or image and send the result where it needs to go next.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/zIqJvnu6QU7VkwKwoxpTHlmm3xuek7bG3OSG6FEsSBrRA5grYgM8FUXJYM5TTKYnl5_cM4o2NRYf-RCgQ_Jg',
      alt: 'PDF Scanner & Editor screenshot showing additional PDF tools',
      caption: 'Keep scanning, conversion, and PDF cleanup inside one Android app.',
    },
  ],
  quickActions: [],
};

const cleanorIosLanding: ProjectLandingContent = {
  storeLabel: 'App Store',
  storeAvailability: 'Free on the App Store',
  productHuntHref: productHuntUrl,
  developerName: 'Cleanor Research Labs L.L.C-FZ',
  iconSrc: `${siteUrl}/app-icons/cleanor-ios.svg`,
  iconAlt: 'Cleanor: Smart Phone Cleaner for iPhone app icon',
  heroSummary:
    'Recover iPhone storage with Smart Cleanup and Deep Analysis across duplicate and similar photos, heavy videos, contacts, and calendar clutter, clean up your inbox with Email Cleaner, and run a full on-device toolkit for compressing, converting, and editing images, video, audio, and PDFs.',
  intro: [
    'Cleanor: Smart Phone Cleaner is an on-device iPhone cleaner built around two engines: Smart Cleanup for obvious wins like exact duplicates, blurry shots, and stale screenshots, and Deep Analysis for the harder work of grouping visually similar photos and videos that a quick scan usually misses.',
    'The App Store build keeps the workflow private: every scan runs locally with no file uploads or collection. You review results in a card-based Swipe Mode, filter by type (bursts, slo-mo, dark videos, screenshots, screen recordings), compress heavy videos with a clear space-savings estimate, and keep sensitive media inside an encrypted Vault protected by biometrics and PIN.',
  ],
  highlights: [
    'Deep Analysis surfaces visually similar photos and videos, not just exact duplicates',
    'Email Cleaner unsubscribes and clears inbox clutter without leaving the app',
    'A full on-device toolkit: compress and convert images, video, and audio, build PDFs, and scan text',
    'Advanced video compression shows estimated space savings before you export',
    'Protected Vault with biometric and PIN security keeps sensitive media separate',
    'Contact merging, calendar cleanup, and a device health dashboard live in the same app',
    'Everything runs on-device with no uploads and no file collection',
  ],
  features: [
    {
      title: 'Clean photos with Smart Cleanup and Deep Analysis',
      description:
        'Two scanning engines split the work between the obvious wins and the harder visual-similarity passes most cleaners skip.',
      bullets: [
        'Smart Cleanup flags exact duplicates, blurry shots, and stale screenshots',
        'Deep Analysis groups visually similar photos and videos for best-shot review',
        'Filter by type: bursts, slo-mo, dark videos, screenshots, and screen recordings',
      ],
    },
    {
      title: 'Review faster in Swipe Mode',
      description:
        'A card-based review flow turns large cleanup passes into quick keep-or-remove decisions instead of long multi-select lists.',
      bullets: [
        'Swipe through grouped results one card at a time',
        'Preview each asset before it leaves the library',
        'Keep the best version and clear the rest in one pass',
      ],
    },
    {
      title: 'Shrink heavy videos with visible savings',
      description:
        'Advanced video compression targets the clips that eat the most space and shows what you would recover before anything is exported.',
      bullets: [
        'See estimated space savings per clip',
        'Compress heavy videos without leaving the app',
        'Pair compression with duplicate and similar-video cleanup',
      ],
    },
    {
      title: 'Keep sensitive media in a protected Vault',
      description:
        'An encrypted Vault gives private photos and videos a separate place on the device, locked behind biometrics and a PIN.',
      bullets: [
        'Biometric and PIN protection on every unlock',
        'Move sensitive media out of the main photo library',
        'Backup support for the Vault alongside standard cleanup',
      ],
    },
    {
      title: 'Tidy contacts, calendar, and device health',
      description:
        'Cleanor covers the smaller data messes that make the phone feel disorganized even when storage is not the headline problem.',
      bullets: [
        'Merge duplicate contacts and remove incomplete entries',
        'Clear older calendar events that no longer matter',
        'Check a device health dashboard with widgets for quick status',
      ],
    },
    {
      title: 'Clean up your inbox with Email Cleaner',
      description:
        'Email Cleaner brings inbox cleanup into the same app, so the mailbox that quietly fills up your storage and attention gets tidied alongside your photos.',
      bullets: [
        'Unsubscribe from newsletters and senders you no longer read',
        'Clear out bulk and promotional clutter in a few taps',
        'Keep the inbox lean without a separate subscription',
      ],
    },
    {
      title: 'Run a full toolkit on your device',
      description:
        'The tools you would normally hunt for online are built in and run locally: the same compression and conversion engine behind the Cleanor web tools, on your phone.',
      bullets: [
        'Compress and convert images, video, and audio to modern formats',
        'Combine photos into PDFs and handle everyday PDF tasks',
        'Scan text from photos, remove backgrounds, and resize for any platform',
      ],
    },
    {
      title: 'Stay private with on-device processing',
      description:
        'Every scan, preview, and cleanup pass runs locally with no file uploads, no collection, and no cloud detour.',
      bullets: [
        'Scans camera roll, contacts, and calendar on the device',
        'No media leaves the iPhone during cleanup',
        'Email Cleaner and the built-in toolkit run on-device too',
      ],
    },
  ],
  screenshots: [
    {
      src: 'https://is1-ssl.mzstatic.com/image/thumb/PurpleSource221/v4/54/48/24/544824c4-2c0d-7879-2ec4-e5ab1212bf94/Analyze.png/460x998bb.webp',
      alt: 'Cleanor for iPhone screenshot showing storage analysis and cleanup overview',
      caption: 'Start with a clear picture of what is consuming space on the iPhone.',
    },
    {
      src: 'https://is1-ssl.mzstatic.com/image/thumb/PurpleSource221/v4/5f/ec/62/5fec6264-707d-3847-8000-e841f5fc9060/Photos.png/460x998bb.webp',
      alt: 'Cleanor for iPhone screenshot showing duplicate and similar photo cleanup',
      caption: 'Review duplicate and similar photos in grouped cleanup flows.',
    },
    {
      src: 'https://is1-ssl.mzstatic.com/image/thumb/PurpleSource211/v4/9b/54/1c/9b541ca3-7811-b3e9-a159-508f0fb63f1d/Videos.png/460x998bb.webp',
      alt: 'Cleanor for iPhone screenshot showing large video cleanup',
      caption: 'Prioritize heavy videos when the fastest storage recovery matters most.',
    },
    {
      src: 'https://is1-ssl.mzstatic.com/image/thumb/PurpleSource221/v4/c5/15/3a/c5153a2c-033f-b497-fb3d-9ccdc389fe1d/Contacts.png/460x998bb.webp',
      alt: 'Cleanor for iPhone screenshot showing duplicate contacts cleanup',
      caption: 'Keep contacts cleaner by merging duplicates and removing stale entries.',
    },
    {
      src: 'https://is1-ssl.mzstatic.com/image/thumb/PurpleSource221/v4/4b/7d/e2/4b7de22d-923a-7e40-8cbf-606834098375/Calendar.png/460x998bb.webp',
      alt: 'Cleanor for iPhone screenshot showing old calendar event cleanup',
      caption: 'Clear older calendar clutter without leaving the same app.',
    },
  ],
  quickActions: [],
  googlePlayHref: withStoreCampaign(cleanorAndroidPlayUrl, 'web-app-landing'),
  googlePlayLabel: 'Get Cleanor for Android on Google Play',
  trustChips: [
    'Free to download',
    'On-device, no uploads',
    'No account required',
    'Live on iPhone and Android',
  ],
  facts: [
    { label: 'Price', value: 'Free to download, optional in-app Premium' },
    { label: 'Platform', value: 'iPhone and Android, both live' },
    { label: 'Privacy', value: 'On-device processing, no account, no uploads' },
    { label: 'Best for', value: 'Duplicate and similar photos, heavy videos' },
    { label: 'Developer', value: 'Cleanor Research Labs' },
    { label: 'Category', value: 'Utilities, phone storage cleaner' },
  ],
  steps: [
    {
      title: 'Scan on-device',
      description:
        'Open Cleanor and run Smart Cleanup for the obvious wins, then Deep Analysis to group visually similar photos and videos. Every scan runs locally, and nothing is uploaded.',
    },
    {
      title: 'Review in Swipe Mode',
      description:
        'Swipe through grouped results one card at a time, preview each asset, and keep the best version. You decide what stays before anything leaves the library.',
    },
    {
      title: 'Recover space and tidy up',
      description:
        'Compress heavy videos with a clear space-savings estimate, merge duplicate contacts, clear stale calendar events, and move private media into the encrypted Vault.',
    },
    {
      title: 'Keep the phone lean',
      description:
        'Check the device health dashboard and widgets, then re-run a quick scan whenever storage starts filling up again. No subscription needed to clean.',
    },
  ],
  faqs: [
    {
      question: 'Is Cleanor free to download?',
      answer:
        'Yes. Cleanor is free to download on the App Store and you can run a storage scan and clean duplicates without paying. Some advanced batches and the encrypted Vault are part of the in-app Premium upgrade, but core cleanup is free.',
    },
    {
      question: 'Does Cleanor upload my photos or videos anywhere?',
      answer:
        'No. Every scan, preview, and cleanup pass runs entirely on the device. Cleanor does not upload your media, does not collect your files, and does not need an account to clean your iPhone.',
    },
    {
      question: 'How is Cleanor different from deleting photos manually?',
      answer:
        'Manual deletion misses near-duplicates and visually similar shots. Cleanor’s Deep Analysis groups similar photos and videos so you can keep the best one and clear the rest, and Swipe Mode turns a large cleanup into quick keep-or-remove decisions.',
    },
    {
      question: 'Will Cleanor delete anything without my approval?',
      answer:
        'No. Cleanor only surfaces candidates for review. You confirm what to remove in Swipe Mode, and items move to your Recently Deleted album first, so nothing is permanently erased without your action.',
    },
    {
      question: 'Is there an Android version of Cleanor?',
      answer:
        'Yes. Cleanor is live on Google Play as Phone Cleaner: CleanUp & Tools, alongside the iPhone app on the App Store. The Android edition shares the same private, on-device approach and adds Secret Storage, an optional Email Cleaner, and a 40+ tool box for images, PDFs, video, and audio.',
    },
    {
      question: 'What can Cleanor clean besides photos?',
      answer:
        'Beyond duplicate and similar photos, Cleanor compresses heavy videos, merges duplicate contacts, clears old calendar clutter, secures sensitive media in an encrypted Vault, and shows a device health dashboard, all in one app.',
    },
    {
      question: 'Does Cleanor work without internet?',
      answer:
        'Yes. Cleanor scans and cleans entirely offline. Because everything runs on the device, you can recover storage with no connection, and no data leaves your iPhone.',
    },
    {
      question: 'How much storage can Cleanor free up?',
      answer:
        'It depends on your library, but duplicate photos, visually similar shots, and heavy videos are usually the biggest wins. Cleanor shows an estimated space saving before you remove anything, so you see the result first.',
    },
  ],
};

const cleanorAndroidLanding: ProjectLandingContent = {
  storeLabel: 'Google Play',
  storeAvailability: 'Free on Google Play',
  developerName: 'Cleanor Labs',
  iconSrc:
    'https://play-lh.googleusercontent.com/JrkUs-OPnOZG5EW9cT09k2F0gKJS4fudpXz-O7bnLJdo1nvP5nVfDX3CtTeUWQvhRHrgNHlXml_Z8TKRT_gFLuo=w240-h480',
  iconAlt: 'Cleanor phone cleaner for Android app icon',
  heroSummary:
    'Free up Android storage on-device: duplicate and similar photos, large videos, screen recordings, screenshots, contacts, calendar clutter, an optional email cleaner, and a 40+ tool box for images, PDFs, video, and audio.',
  intro: [
    'Cleanor for Android finds the files that quietly fill a phone, repeated and visually similar photos, burst shots, blurry and dark frames, large videos, screen recordings, and stale screenshots, then puts them into focused review passes instead of one risky delete-all action.',
    'Everything is processed on the device, and nothing is removed until Android shows its own delete confirmation. Beyond cleanup, the app carries a full utility box: image compression and conversion, resize and crop, collage, images to PDF, PDF merge and split, on-device scan-to-text and background removal, passport photos, metadata stripping, video compression, and audio conversion.',
  ],
  highlights: [
    'Live on Google Play, free to download',
    'Duplicate, similar, burst, blurry, and dark photo detection',
    'Large videos, screen recordings, short clips, and slow-motion cleanup',
    'Swipe Mode for one-by-one keep-or-delete decisions',
    '40+ built-in tools for images, PDFs, video, and audio',
    'Secret Storage behind a PIN for private media',
    'Optional Email Cleaner that reads senders and headers only',
    'On-device processing, deletions confirmed by Android itself',
  ],
  features: [
    {
      title: 'Clean the photo library with more confidence',
      description:
        'The photo pass groups what a manual scroll always misses, so you keep the best frame instead of deleting one file at a time.',
      bullets: [
        'Group duplicate and visually similar photos together',
        'Filter burst shots, blurry frames, and dark photos',
        'Sweep screenshots and large images in their own pass',
      ],
    },
    {
      title: 'Target the heaviest storage wins first',
      description:
        'Video is usually where the gigabytes are, so large clips and recordings get their own review flows and a compressor with a before-and-after size estimate.',
      bullets: [
        'Surface large videos, duplicates, and near-identical clips',
        'Clear screen recordings, short clips, slow-motion, and timelapse',
        'Compress a heavy clip instead of deleting it outright',
      ],
    },
    {
      title: 'Carry a real tool box, not just a cleaner',
      description:
        'The same app covers the small jobs that usually mean a second download or a desktop trip.',
      bullets: [
        'Image compressor, converter, resize, crop, effects, and collage',
        'Images to PDF, plus PDF merge, compress, and split',
        'Scan to Text and Background Remover, both on-device',
        'Passport and ID photos, social and marketplace size presets',
      ],
    },
    {
      title: 'Keep private files and inbox clutter in check',
      description:
        'Storage is not the only mess on a phone, so the app also covers private media, contacts, calendar, and an optional mailbox pass.',
      bullets: [
        'Secret Storage keeps chosen photos and videos behind a PIN',
        'Privacy Studio strips location and metadata before sharing',
        'Merge duplicate contacts and clear outdated calendar events',
        'Email Cleaner unsubscribes and bulk-trashes by sender, never reading message bodies',
      ],
    },
  ],
  screenshots: [
    {
      src: 'https://play-lh.googleusercontent.com/tC8CJy_R8CO76hIPZbr_F2YaUgRJH8IajHA22C0IOoWmpzAahIP9aHDJT3HBwbH1z2iFTFYEiiIr1g8fozi8k-c',
      alt: 'Cleanor for Android screenshot showing storage before and after cleanup with similar photos, videos, live photos, and screenshots',
      caption:
        'See the before-and-after storage picture, then open the pass that frees the most space.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/ott4_o6Hh0xhz2pBKtd2AsuAAMw3QcL-WePL9iznCR7S-zq3OaJa6xsBhj67ZznWxt7xIvc0lWHcTj3aBDFxIsc',
      alt: 'Cleanor for Android screenshot showing grouped similar photos with the best shot marked in each group',
      caption:
        'Similar photos arrive grouped with a suggested best shot, so you keep one and clear the rest.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/siojBsn14pfaxwvw3fRr03sk3V6bPza5neJ1krpdgKW2XORnc3m1mWp1Gr1O17Pej-r1ihzKBg2goLbQmRP3DQ',
      alt: 'Cleanor for Android screenshot showing Swipe Mode with keep and delete card gestures',
      caption: 'Swipe Mode turns the leftovers into fast keep-or-delete card decisions.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/8TNBYTV_TJ66Dfjnix4cYmXVQE2w_i788DM9k4SsznTa9FP8SbRhRwqNIjgNQlIh9zo8VUExvY4Z1UlvbDkybYc',
      alt: 'Cleanor for Android screenshot showing the All Tools grid with image, PDF, video, and contact utilities',
      caption:
        'Over 40 tools sit in the same app, from image conversion to PDF merge and text OCR.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/ZbTrdTrOULDe__Hkxq4QaA3Yt_w54VppGOjAYNQwN1-n9UdJnvcAHpq9k8gWLaGGgBE-WBU1XbZ-hX5G0Yh-Gw',
      alt: 'Cleanor for Android screenshot showing video compression with the estimated size after export',
      caption: 'Video compression shows the projected size before you commit to the export.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/VlfnminpqlDoB_nPf0hmKaOzbkYriogExGVQtqScxEk1CDo54xZItsxz5U_FSLcuCZpwcHkMDknvAEds_RO7RQ',
      alt: 'Cleanor for Android screenshot showing Secret Storage set up with a PIN code for private photos and videos',
      caption: 'Secret Storage keeps chosen photos and videos behind a PIN on the device.',
    },
    {
      src: 'https://play-lh.googleusercontent.com/xk6-oWDUabe164st3J5xVUXn7tzQDL0gDYidiwnRW4D5vrfHlVUbXA3_sZMN9uUOPkc151kTpTfvvW7qBZucRw',
      alt: 'Cleanor for Android screenshot showing the Email Cleaner grouping spam, promotions, news, and social mail by sender',
      caption:
        'The optional Email Cleaner groups mail by sender so you can unsubscribe and bulk-trash in one pass.',
    },
  ],
  quickActions: [],
  googlePlayHref: withStoreCampaign(cleanorAndroidPlayUrl, 'web-app-landing'),
  googlePlayLabel: 'Get Cleanor for Android on Google Play',
  trustChips: [
    'Free to download',
    'On-device, no uploads',
    'Android confirms every deletion',
    'Live on Google Play',
  ],
  facts: [
    { label: 'Price', value: 'Free to download, optional in-app subscription' },
    { label: 'Platform', value: 'Android, live on Google Play' },
    { label: 'Package', value: androidPackageName },
    { label: 'Privacy', value: 'On-device processing, personal content never uploaded' },
    { label: 'Best for', value: 'Duplicate and similar photos, heavy videos, screenshots' },
    { label: 'Developer', value: 'Cleanor Labs' },
  ],
  steps: [
    {
      title: 'Scan on-device',
      description:
        'Open Cleanor and let it index photos, videos, screenshots, and screen recordings locally. Nothing is uploaded, and the scan works without a connection.',
    },
    {
      title: 'Review the grouped results',
      description:
        'Work through similar and duplicate groups, large videos, and screenshot piles. Each group suggests a best shot so the decision is a confirmation, not a hunt.',
    },
    {
      title: 'Confirm the deletion',
      description:
        'Selected files are removed through the Android system confirmation dialog, so nothing leaves the device storage without your explicit approval.',
    },
    {
      title: 'Use the tools instead of deleting',
      description:
        'When a file is worth keeping, compress the video, shrink the image, convert the format, or move it into Secret Storage rather than losing it.',
    },
  ],
  faqs: [
    {
      question: 'Is Cleanor for Android free?',
      answer:
        'Yes. Cleanor is free to download on Google Play and the core scan-and-clean flow works without paying. Some features are part of an optional subscription billed through Google Play, which you can manage or cancel in the Play Store at any time.',
    },
    {
      question: 'Does Cleanor upload my photos or videos?',
      answer:
        'No. Photos, videos, contacts, calendar entries, and email metadata are processed on the device. Cleanor does not upload your personal content to its own servers.',
    },
    {
      question: 'Can Cleanor delete something by accident?',
      answer:
        'Files are removed through Android\u2019s own system delete confirmation, so nothing is deleted without you approving it in that dialog. Cleanor only proposes candidates and groups them for review.',
    },
    {
      question: 'What does the Email Cleaner actually read?',
      answer:
        'It is optional and off until you connect a Google account. When connected, Cleanor reads message senders and headers to build sender lists, never the content of your emails, and you can disconnect at any time.',
    },
    {
      question: 'How is it different from the iPhone version?',
      answer:
        'Both editions share the same on-device cleanup approach. The Android build adds Secret Storage, the optional Email Cleaner, and a wider 40+ tool box for images, PDFs, video, and audio. The iPhone app is on the App Store.',
    },
    {
      question: 'What is the package name on Google Play?',
      answer:
        'The Android app is published as app.cleanor.phonecleaner by Cleanor Labs, listed on Google Play as Phone Cleaner: CleanUp & Tools.',
    },
  ],
};

// Full catalog kept for landing assets/types; the site surfaces the two live
// Cleanor store listings, /cleanor-ios for the App Store app and /cleanor for the
// Google Play app. Other entries are retired from nav/sitemap/pages and their
// /projects/* URLs 301 to /cleanor-ios (see the redirect config).
const allProjects: Project[] = [
  {
    slug: 'clenoir',
    routeSlug: 'cleanor-ios',
    legacyRouteSlugs: ['clenoir'],
    name: 'Cleanor: Smart Phone Cleaner',
    stage: 'Live',
    platform: 'iOS & Android',
    category: 'UtilitiesApplication',
    operatingSystem: 'iOS, Android',
    headline:
      'One trusted, on-device cleaner for media-heavy phones. It runs Smart Cleanup, Deep Analysis, Swipe Mode review, video compression, and an encrypted Vault. Live on both the App Store and Google Play.',
    summary:
      'Cleanor is a single app for iPhone and Android that recovers storage entirely on-device: Smart Cleanup and Deep Analysis for duplicate and visually similar media, Swipe Mode review, video compression, contact and calendar tidy-up, and an encrypted Vault. Nothing is uploaded.',
    description: [
      'Cleanor recovers storage with two engines: Smart Cleanup for obvious wins like exact duplicates, blurry shots, and stale screenshots, and Deep Analysis for the visually similar photos and videos a quick scan misses. Swipe Mode turns the rest into fast keep-or-remove card decisions.',
      'Advanced video compression shows estimated space savings before export, and an encrypted Vault keeps sensitive media behind biometrics and a PIN. Contact merging, calendar cleanup, and a device-health dashboard live in the same app, and every scan runs on-device with no file uploads. Cleanor is live on the App Store for iPhone and on Google Play for Android.',
    ],
    seoTitle: 'Cleanor: Phone Cleaner for iPhone & Android, On-Device',
    seoDescription:
      'Cleanor is a single on-device cleaner for iPhone and Android: Smart Cleanup, Deep Analysis for similar photos and videos, Swipe Mode, video compression, and an encrypted Vault. Live on the App Store and Google Play.',
    keywords: [
      'iPhone cleaner app',
      'iPhone storage cleaner',
      'photo cleaner for iPhone',
      'duplicate photo cleaner',
      'similar photos cleaner',
      'large videos cleaner',
      'iPhone video compressor',
      'encrypted photo vault iPhone',
      'swipe mode photo cleanup',
      'cleanor for iphone',
    ],
    highlights: [
      'One app, two platforms, live on the App Store and Google Play',
      'Smart Cleanup and Deep Analysis for duplicate and visually similar media',
      'Swipe Mode card review, filters for bursts, slo-mo, dark videos, and screenshots',
      'Advanced video compression with space-savings estimates',
      'Encrypted Vault with biometric and PIN protection',
      'Contact merging, calendar cleanup, and device health dashboard',
      'On-device processing — no file uploads',
    ],
    href: withStoreCampaign(clenoirAppStoreUrl, 'web-app-landing'),
    ctaLabel: 'Download on the App Store',
    // Real App Store rating (US storefront, id6744980716). Refresh from the
    // store when it moves materially — keep these numbers genuine.
    rating: { value: 4.7, count: 55 },
    landing: cleanorIosLanding,
  },
  {
    slug: 'cleanor',
    routeSlug: 'cleanor',
    name: 'Cleanor for Android',
    stage: 'Live',
    platform: 'Android',
    category: 'UtilitiesApplication',
    operatingSystem: 'Android',
    headline:
      'An Android phone cleaner that frees storage on-device, with a 40+ tool box for images, PDFs, video, and audio in the same app.',
    summary:
      'Free up Android storage by clearing duplicate and similar photos, large videos, screen recordings, and screenshots, then keep the rest tidy with built-in image, PDF, video, and audio tools.',
    description: [
      'Cleanor for Android finds the files that quietly fill a phone: repeated and visually similar photos, burst shots, blurry and dark frames, large videos, screen recordings, short clips, and stale screenshots. Everything is grouped into focused review passes with a suggested best shot, and Swipe Mode turns whatever is left into quick keep-or-delete decisions.',
      'Scanning runs on the device and files are removed only through Android\u2019s own delete confirmation. The same app carries over 40 tools, including image compression and conversion, resize and crop, collage, images to PDF, PDF merge and split, on-device scan-to-text and background removal, passport photos, metadata stripping, video compression, and audio conversion, plus Secret Storage behind a PIN and an optional Email Cleaner that only reads senders and headers.',
    ],
    seoTitle: 'Cleanor for Android | Phone Cleaner and 40+ Tools on Google Play',
    seoDescription:
      'Cleanor for Android frees storage on-device: duplicate and similar photos, large videos, screen recordings, and screenshots, plus 40+ image, PDF, video, and audio tools. Free on Google Play.',
    keywords: [
      'android phone cleaner',
      'free up android storage',
      'duplicate photo cleaner android',
      'similar photos cleaner android',
      'clean up screenshots android',
      'large video cleaner android',
      'android cleanup app',
      'cleanor android',
    ],
    highlights: [
      'Live on Google Play, free to download',
      'Duplicate, similar, burst, blurry, and dark photo detection',
      'Large videos, screen recordings, and screenshot cleanup passes',
      'Swipe Mode for fast keep-or-delete decisions',
      '40+ tools for images, PDFs, video, and audio in the same app',
      'Secret Storage behind a PIN and an optional Email Cleaner',
      'On-device processing, every deletion confirmed by Android',
    ],
    href: withStoreCampaign(cleanorAndroidPlayUrl, 'web-app-landing'),
    androidApplicationId: androidPackageName,
    ctaLabel: 'Get it on Google Play',
    landing: cleanorAndroidLanding,
  },
  {
    slug: 'mindalarm',
    routeSlug: 'alarm-clock-focus-calm-sleep',
    name: 'Alarm Clock: Focus Calm Sleep',
    stage: 'Live',
    platform: 'Android',
    category: 'UtilitiesApplication',
    operatingSystem: 'Android',
    headline:
      'An Android alarm clock app for calmer wake-ups, focused routines, and lower-friction daily timing.',
    summary:
      'A calmer Android alarm clock with mission alarms, focus timing, breathing modes, and sleep sounds in one routine app.',
    description: [
      'Alarm Clock: Focus Calm Sleep helps people wake up more reliably, stay focused during the day, and wind down at night without juggling separate apps for each job.',
      'The app is live on Google Play and now gets the same dedicated landing treatment as the other Android products on cleanor.app, with store assets and a direct install path.',
    ],
    seoTitle: 'Alarm Clock: Focus Calm Sleep for Android | Alarm App from Cleanor Labs',
    seoDescription:
      'Alarm Clock: Focus Calm Sleep is an Android alarm clock app from Cleanor Labs focused on reliable wake-ups, calmer routines, and a cleaner daily timing flow.',
    keywords: [
      'android alarm clock',
      'alarm app android',
      'focus sleep alarm',
      'cleanor labs alarm clock',
      'calm wake up app',
    ],
    highlights: [
      'Reliable Android wake-up flow',
      'Mission alarms, focus timer, and sleep support in one app',
      'Part of the broader Cleanor Labs Android utility lineup',
    ],
    href: withStoreCampaign(
      'https://play.google.com/store/apps/details?id=com.cleanorlabs.mindalarm',
      'web-app-landing',
    ),
    androidApplicationId: mindAlarmAndroidPackageName,
    ctaLabel: 'Get it on Google Play',
    landing: mindAlarmLanding,
  },
  {
    slug: 'image-to-pdf-converter',
    routeSlug: 'image-to-pdf-converter',
    name: 'Image to PDF Converter',
    stage: 'In review',
    platform: 'Android',
    category: 'BusinessApplication',
    operatingSystem: 'Android',
    headline:
      'An Android image-to-PDF app for turning photos, screenshots, and document images into clean PDF files in a few taps.',
    summary:
      'An Android utility for converting images into PDF files for sharing, saving, and lightweight document workflows.',
    description: [
      'Image to PDF Converter is positioned as a practical Android utility for turning gallery images, screenshots, and captured pages into compact PDF output without a heavy desktop workflow.',
      'The listing adds a more document-focused product line to the Projects section while the app is still under Google Play review.',
    ],
    seoTitle: 'Image to PDF Converter for Android | Photo and Scan PDF Utility',
    seoDescription:
      'Image to PDF Converter is an Android app for turning photos, screenshots, and document images into clean PDF files for sharing and lightweight document work.',
    keywords: [
      'image to pdf converter android',
      'photo to pdf app',
      'jpg to pdf android',
      'document image to pdf',
      'pdf converter app',
    ],
    highlights: [
      'Convert photos and screenshots into PDF files',
      'Built for lightweight document export on Android',
      'Currently in review for Google Play release',
    ],
    href: withStoreCampaign(
      'https://play.google.com/store/apps/details?id=app.cleanor.pdftools.imagetopdf',
      'web-app-landing',
    ),
    androidApplicationId: 'app.cleanor.pdftools.imagetopdf',
    ctaLabel: 'View Google Play listing',
  },
  {
    slug: 'pdf-scanner-editor',
    routeSlug: 'pdf-scanner-editor',
    name: 'PDF Scanner & Editor',
    stage: 'Live',
    platform: 'Android',
    category: 'BusinessApplication',
    operatingSystem: 'Android',
    headline:
      'An Android PDF scanner app for capture, cleanup, and document export from one simpler scanning flow.',
    summary:
      'Scan documents, convert images to PDF, extract text with OCR, and manage files from a cleaner Android document workflow.',
    description: [
      'PDF Scanner & Editor turns the phone into a practical mobile scanner for receipts, notes, contracts, IDs, and multi-page document exports without relying on a desktop setup.',
      'The app is live on Google Play and now has the same dedicated landing treatment as the other Android products on cleanor.app, with store assets and a direct install path.',
    ],
    seoTitle: 'PDF Scanner & Editor for Android | Mobile Scan and PDF Export App',
    seoDescription:
      'PDF Scanner & Editor is an Android app for scanning documents, cleaning page images, and exporting PDF files from a fast mobile workflow.',
    keywords: [
      'pdf scanner android',
      'document scanner app',
      'scan to pdf android',
      'pdf editor scanner',
      'mobile pdf scanner',
    ],
    highlights: [
      'Mobile document scanning and PDF export',
      'OCR, image-to-PDF conversion, and file organization',
      'Live Android productivity app on Google Play',
    ],
    href: withStoreCampaign(
      'https://play.google.com/store/apps/details?id=app.cleanor.pdfscanner.imageconverter',
      'web-app-landing',
    ),
    androidApplicationId: 'app.cleanor.pdfscanner.imageconverter',
    ctaLabel: 'Get it on Google Play',
    landing: pdfScannerLanding,
  },
  {
    slug: 'photo-compressor-resizer',
    routeSlug: 'photo-compressor-resizer',
    name: 'Photo Compressor & Resizer',
    stage: 'Live',
    platform: 'Android',
    category: 'PhotographyApplication',
    operatingSystem: 'Android',
    headline:
      'An Android photo utility for reducing file size, resizing images, and preparing pictures for upload or sharing.',
    summary:
      'Reduce photo size, resize images, and export lighter files for sharing, uploads, and cleaner storage on Android.',
    description: [
      'Photo Compressor & Resizer helps people shrink image files, resize dimensions, and prepare lighter exports from the phone without a bloated editing workflow.',
      'The app is live on Google Play and now has a dedicated landing page on cleanor.app with store assets, clearer product framing, and a direct install path.',
    ],
    seoTitle: 'Photo Compressor & Resizer for Android | Image Size and Resize Utility',
    seoDescription:
      'Photo Compressor & Resizer is an Android app for shrinking image size, resizing photos, and preparing pictures for sharing or upload with less friction.',
    keywords: [
      'photo compressor android',
      'image resizer app',
      'compress photos android',
      'resize image android',
      'photo size reducer',
    ],
    highlights: [
      'Compress and resize photos on Android',
      'Live Google Play listing with direct install path',
      'Built for quick share and upload prep',
      'Android app indexing and verified website association',
    ],
    href: withStoreCampaign(
      'https://play.google.com/store/apps/details?id=app.cleanor.photocompressor',
      'web-app-landing',
    ),
    androidApplicationId: photoCompressorAndroidPackageName,
    ctaLabel: 'Get it on Google Play',
    landing: photoCompressorLanding,
  },
  {
    slug: 'video-compressor-resizer',
    routeSlug: 'video-compressor-resizer',
    name: 'Video Compressor & Resizer',
    stage: 'Live',
    platform: 'Android',
    category: 'VideoApplication',
    operatingSystem: 'Android',
    headline:
      'An Android video utility for reducing clip size, changing resolution, and making large files easier to share or store.',
    summary:
      'Compress videos, resize clips, and convert heavy files into lighter exports that are easier to share, upload, and keep on Android.',
    description: [
      'Video Compressor & Resizer helps people cut file size, change video format, and prep large clips for social posting, messaging, and lighter device storage without a desktop workflow.',
      'The app is live on Google Play and now has a dedicated landing page on cleanor.app with store assets, stronger product framing, and a direct install path.',
    ],
    seoTitle: 'Video Compressor & Resizer for Android | Video Size Reduction Utility',
    seoDescription:
      'Video Compressor & Resizer is an Android app for reducing video size, resizing clips, and preparing large files for sharing, upload, and lighter storage use.',
    keywords: [
      'video compressor android',
      'video resizer app',
      'compress video android',
      'reduce video size',
      'video size reducer app',
    ],
    highlights: [
      'Reduce large video size on Android',
      'Resize clips for easier sharing and upload',
      'Convert formats, trim clips, and extract audio',
      'Live media utility on Google Play',
    ],
    href: withStoreCampaign(
      'https://play.google.com/store/apps/details?id=com.cleanorlabs.videocompressor',
      'web-app-landing',
    ),
    androidApplicationId: videoCompressorAndroidPackageName,
    ctaLabel: 'Get it on Google Play',
    landing: videoCompressorLanding,
  },
  {
    slug: 'another-you',
    routeSlug: 'another-you',
    name: 'Another You',
    stage: 'Coming soon',
    platform: 'AI photo editor',
    category: 'PhotoApplication',
    operatingSystem: 'iOS, Android, Web',
    headline:
      'An AI photo editor exploring identity, style, portrait variation, and fast creative edits.',
    summary:
      'A coming-soon AI photo editor from the lab, focused on approachable generation and editing flows.',
    description: [
      'Another You is the lab’s next consumer product, built around practical AI photo transformations.',
      'The initial release will focus on fast creative edits, easy outputs, and a strong mobile-first publishing loop.',
    ],
    seoTitle: 'Another You | AI Photo Editor Coming Soon',
    seoDescription:
      'Another You is a coming-soon AI photo editor from Cleanor Research Labs focused on creative edits and mobile-first image workflows.',
    keywords: [
      'AI photo editor',
      'photo editing app',
      'AI portrait editor',
      'coming soon app',
      'another you app',
    ],
    highlights: [
      'AI-assisted portrait and style editing',
      'Mobile-first creative workflow',
      'Coming soon landing page for early branded search demand',
    ],
    ctaLabel: 'Coming soon',
  },
];

// One app, two platforms — the only product the site + blog are dedicated to.
export const projects: Project[] = allProjects.filter(
  (project) => project.slug === 'clenoir' || project.slug === 'cleanor',
);

export function getProjectBySlug(slug: string) {
  return projects.find((project) => project.slug === slug);
}

export function getProjectByRouteSlug(routeSlug: string) {
  return projects.find(
    (project) => project.routeSlug === routeSlug || project.legacyRouteSlugs?.includes(routeSlug),
  );
}

export function getProjectByIdentifier(identifier: string) {
  return getProjectBySlug(identifier) || getProjectByRouteSlug(identifier);
}

export function getProjectRouteSlug(projectOrIdentifier: Project | string) {
  if (typeof projectOrIdentifier !== 'string') {
    return projectOrIdentifier.routeSlug;
  }

  return getProjectByIdentifier(projectOrIdentifier)?.routeSlug;
}

export function resolveProjectRoute(identifier: string) {
  const project = getProjectByRouteSlug(identifier);

  if (!project) {
    return null;
  }

  return {
    project,
    canonicalRouteSlug: project.routeSlug,
    isLegacyRoute: project.routeSlug !== identifier,
  };
}

export function getProjectPath(projectOrIdentifier: Project | string) {
  const routeSlug = getProjectRouteSlug(projectOrIdentifier);
  return routeSlug ? `/projects/${routeSlug}` : undefined;
}

export function getProjectDirectPath(projectOrIdentifier: Project | string) {
  const routeSlug = getProjectRouteSlug(projectOrIdentifier);
  return routeSlug ? `/${routeSlug}` : undefined;
}

export function getProjectPreferredPath(projectOrIdentifier: Project | string) {
  return getProjectDirectPath(projectOrIdentifier) || getProjectPath(projectOrIdentifier);
}

export function getProjectUrl(projectOrIdentifier: Project | string) {
  const path = getProjectPath(projectOrIdentifier);
  return path ? absoluteUrl(path) : undefined;
}

export function getProjectDirectUrl(projectOrIdentifier: Project | string) {
  const path = getProjectDirectPath(projectOrIdentifier);
  return path ? absoluteUrl(path) : undefined;
}

export function getProjectPreferredUrl(projectOrIdentifier: Project | string) {
  const path = getProjectPreferredPath(projectOrIdentifier);
  return path ? absoluteUrl(path) : undefined;
}

export function buildSmartAppBannerMetaContent(
  appId: string,
  appArgument: string,
  options?: { appClipBundleId?: string; appClipDisplay?: 'card' | 'compact' },
) {
  const parts = [`app-id=${appId}`, `app-argument=${appArgument}`];
  if (options?.appClipBundleId) {
    parts.push(`app-clip-bundle-id=${options.appClipBundleId}`);
    parts.push(`app-clip-display=${options.appClipDisplay ?? 'card'}`);
  }
  return parts.join(', ');
}

export function getProjectActionPath(projectOrIdentifier: Project | string, action: string) {
  const projectPath = getProjectPath(projectOrIdentifier);
  return projectPath ? `${projectPath}/${action}` : undefined;
}

export function normalizeProjectPagePath(path: string) {
  if (!path.startsWith('/projects/')) {
    return path;
  }

  const suffix = path.slice('/projects/'.length);
  const [identifier, ...rest] = suffix.split('/');

  if (!identifier) {
    return path;
  }

  const directPath = getProjectDirectPath(identifier);

  if (!directPath) {
    return path;
  }

  return `${directPath}${rest.length ? `/${rest.join('/')}` : ''}`;
}

export function getProjectAppEventsPath(projectOrIdentifier: Project | string, eventSlug?: string) {
  const routeSlug = getProjectRouteSlug(projectOrIdentifier);

  if (!routeSlug) {
    return undefined;
  }

  return eventSlug ? `/app-events/${routeSlug}/${eventSlug}` : `/app-events/${routeSlug}`;
}

export function getProjectAppEventsUrl(projectOrIdentifier: Project | string, eventSlug?: string) {
  const path = getProjectAppEventsPath(projectOrIdentifier, eventSlug);
  return path ? absoluteUrl(path) : undefined;
}

export function absoluteUrl(path = '/') {
  return path.startsWith('http') ? path : `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

export type SiteSectionLink = {
  href: string;
  label: string;
  description: string;
};

export const primaryNavigationLinks: SiteSectionLink[] = [
  {
    href: '/tools',
    label: 'Tools',
    description: 'Open browser-based PDF, image, OCR, privacy, and export utilities.',
  },
  {
    href: '/projects',
    label: 'Apps',
    description: 'Cleanor and the other Cleanor Labs apps for iPhone and Android.',
  },
  {
    href: '/extensions',
    label: 'Extensions',
    description:
      'Bring Cleanor into your stack: the MCP server, WordPress plugin, and Chrome extension.',
  },
  {
    href: '/blog',
    label: 'Blog',
    description: 'Read articles, launch notes, and practical writing from the lab.',
  },
  {
    href: '/research',
    label: 'Research',
    description: 'Original data studies on phone storage, with downloadable PDF editions.',
  },
  {
    href: '/trends',
    label: 'Trends',
    description: 'The Cleanor Search Index: AI tool popularity by real Google search demand.',
  },
  {
    href: '/community',
    label: 'Community',
    description: 'Ask the team, report a bug or suggest a tool. We answer every thread.',
  },
  // Solutions, Pricing, and Support were removed from the primary nav (they stay
  // reachable from the footer). Pricing content now lives on the app pages.
];

export function getLanguageAlternates(path = '/') {
  return {
    canonical: absoluteUrl(path),
  } as const;
}

export const faqSpeakableSpecification = {
  '@type': 'SpeakableSpecification',
  cssSelector: ['.faq-item h3', '.faq-item p', '.faq-question', '.faq-answer', '[data-speakable]'],
} as const;

export const siteTitle = 'Cleanor Research Labs';
export const siteDescription =
  'Cleanor Research Labs offers online PDF, image, OCR, and privacy tools, plus app pages, pricing, comparisons, and support for faster document and media workflows.';
export const siteKeywords = [
  'online pdf tools',
  'ocr tools',
  'image and text tools',
  'photo and video tools',
  'marketplace image prep',
  'seller tools',
  'browser-first image tools',
  'text cleanup tools',
  'privacy tools',
  'phone cleaner app',
  'image redaction',
  'Cleanor Research Labs',
];
export const ogTitle = 'Cleanor Research Labs | Online PDF, Image, OCR, and Privacy Tools';
export const ogDescription =
  'Open browser-based PDF, image, OCR, and privacy tools, explore Cleanor Labs apps, compare options, check pricing, and reach support from one clear navigation layer.';
// Third-party entity profiles. Listing these under the Organization `sameAs`
// helps Google's Knowledge Graph and AI answer engines reconcile cleanor.app
// with the same entity across the web (the Wikidata item carries the verifiable
// App Store app ID, which closes the loop).
export const crunchbaseUrl = 'https://www.crunchbase.com/organization/cleanor-research-labs';
export const wikidataUrl = 'https://www.wikidata.org/wiki/Q140366677';
// productHuntUrl is declared next to cleanorAndroidPlayUrl above.

export const storeAndSocialUrls = [
  ...projects.flatMap((project) => (project.href ? [project.href] : [])),
  linkedInCompanyUrl,
  facebookPageUrl,
  xProfileUrl,
  instagramProfileUrl,
  youtubeChannelUrl,
  githubOrgUrl,
  crunchbaseUrl,
  wikidataUrl,
  productHuntUrl,
];

export const socialProfiles = [
  { label: 'X', href: xProfileUrl },
  { label: 'LinkedIn', href: linkedInCompanyUrl },
  { label: 'Instagram', href: instagramProfileUrl },
  { label: 'YouTube', href: youtubeChannelUrl },
  { label: 'Facebook', href: facebookPageUrl },
  { label: 'GitHub', href: githubOrgUrl },
] as const;

function readEnv(...names: string[]) {
  for (const name of names) {
    const value = process.env[name]?.trim();

    if (value) {
      return value;
    }
  }

  return undefined;
}

export const secretApiToken = readEnv('SECRET_API_TOKEN');
export const cloudflareImageDeliveryHash = readEnv(
  'NEXT_PUBLIC_CLOUDFLARE_IMAGE_DELIVERY_HASH',
  'CLOUDFLARE_IMAGE_DELIVERY_HASH',
);
